import { reactive, set, del } from 'vue';
import { SecureDocumentModel } from '../../../shared/models/secureDocument.model';
import chunk from 'lodash/chunk';
import { CreateEsdhExportJobRequest } from '../../../shared/models/requests/createEsdhExportJob.request';

type CaseNumberError = 'REQUIRED' | null;

export interface EsdhExportDocumentChild {
  childId: number;
  selected: boolean;
  caseReference?: string;
}
export interface EsdhExportDocument {
  documentId: number;
  children: Record<number, EsdhExportDocumentChild>;
}

const selectedDocuments: Record<number, EsdhExportDocument> = reactive<Record<number, EsdhExportDocument>>({});
const caseNumberErrors = reactive<Record<number, Record<number, CaseNumberError>>>({});

const addDocument = (document: SecureDocumentModel) => {
  set(selectedDocuments, document.id, {
    documentId: document.id,
    children: Object.fromEntries(
      document.associatedInstitutionProfiles.map(profile => [
        profile.id,
        { childId: profile.id, selected: true, caseReference: null },
      ])
    ),
  });
};

const removeDocument = (documentId: number) => {
  del(selectedDocuments, documentId);
};

const updateCaseReference = (documentId: number, childId: number, caseReference: string) => {
  if (selectedDocuments[documentId]?.children[childId]) {
    selectedDocuments[documentId].children[childId].caseReference = caseReference;
  }
};

const clearAll = () => {
  for (const key in selectedDocuments) {
    del(selectedDocuments, key);
  }
  for (const key in caseNumberErrors) {
    del(caseNumberErrors, key);
  }
};

const toggleChild = (documentId: number, childId: number, isSelected: boolean) => {
  if (!selectedDocuments[documentId]) {
    return;
  }
  selectedDocuments[documentId].children[childId].selected = isSelected;
};

const validateCaseReferences = () => {
  clearErrors();
  let isValid = true;
  for (const documentId in selectedDocuments) {
    const document = selectedDocuments[documentId];

    for (const childId in document.children) {
      const child = selectedDocuments[documentId]?.children[childId];
      if (!child || !child.selected) {
        continue;
      }
      const isCaseValid = validateCaseReference(Number(documentId), Number(childId));
      if (!isCaseValid) {
        isValid = false;
      }
    }
  }
  return isValid;
};

const validateCaseReference = (documentId: number, childId: number): boolean => {
  if (!caseNumberErrors[documentId]) {
    set(caseNumberErrors, documentId, {});
  }
  const caseReference = selectedDocuments[documentId].children[childId].caseReference?.trim() ?? null;
  if (caseReference === '') {
    set(caseNumberErrors[documentId], Number(childId), 'REQUIRED');
    return false;
  } else {
    set(caseNumberErrors[documentId], Number(childId), null);
    return true;
  }
};

const clearErrors = () => {
  for (const documentId in caseNumberErrors) {
    for (const childId in caseNumberErrors[documentId]) {
      set(caseNumberErrors[documentId], Number(childId), null);
    }
  }
};

const prepareCreateEsdhExportJobRequests = (): CreateEsdhExportJobRequest[] => {
  const flatRequests = Object.values(selectedDocuments).map(document => ({
    secureDocumentId: document.documentId,
    regardingChildren: Object.values(document.children)
      .filter(child => child.selected)
      .map(child => ({
        childId: child.childId,
        caseReference: child.caseReference,
      })),
  }));

  return chunk(flatRequests, 10).map(chunkedDocs => ({
    secureDocumentsWithRegardingChildren: chunkedDocs,
  }));
};

const getSelectedChildIds = (documentId: number): number[] => {
  const document = selectedDocuments[documentId];
  if (!document) {
    return [];
  }
  return Object.values(document.children)
    .filter(child => child.selected)
    .map(child => child.childId);
};

export const useEsdhExport = () => ({
  selectedDocuments,
  caseNumberErrors,
  addDocument,
  updateCaseReference,
  removeDocument,
  clearAll,
  toggleChild,
  validateCaseReferences,
  validateCaseReference,
  clearErrors,
  prepareCreateEsdhExportJobRequests,
  getSelectedChildIds,
});
